import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import { H3 } from "@amzn/stencil-react-components/text";
import { Card } from "@amzn/stencil-react-components/card";
import { renderBGColor } from "../../task/ui-redesign";
import { getRegionFromCookie } from "../../../utility/locale-helper";
import useArb from "../../../hooks/use-arb";
import { getSettings, Stage } from "../../../helpers/get-settings";
import { getCandidateData } from "../../../utility/candidate-data";
import { getCaasInfo } from "../../../service/get-caas-info";
import { getJobType, getTaskDefinitionsFromConfig, isCustomerService } from "../../../utility/app-config-helper";
import {
  CUSTOMER_SERVICE,
  JobType,
  LAUNCHED_COUNTRY,
  RIGHT_TO_WORK_TASK_DEFINITION_ID,
} from "../../../config/appConfigConstants";
import "../style.scss";
import { I9TaskCardProps } from "../../../utility/types/common";
import { GbNonCompleteInstruction } from "./InstructionsMapping/NotComplete/GbNonCompleteInstruction";
import { getUSChecklistMappingCAAS } from "./ChecklistMapping/CAAS/US";
import { getGBChecklistMappingCAAS } from "./ChecklistMapping/CAAS/GB";
import { getUSChecklistMappingDefault } from "./ChecklistMapping/Default/US";
import { getGBChecklistMappingDefault } from "./ChecklistMapping/Default/GB";
import { getUSInstructionMapping } from "./InstructionsMapping/US";
import { getGBInstructionMapping } from "./InstructionsMapping/GB";
import { ErrorCode } from "../i9-error-message";

export const I9TaskCard = ({ caasPath, status, shouldOverrideStatus }: I9TaskCardProps) => {
  const bundle = useArb("HVHChecklistPantherResource");

  const { applicationId } = useParams<{
    applicationId: string;
  }>();

  const taskDefinitions = getTaskDefinitionsFromConfig(RIGHT_TO_WORK_TASK_DEFINITION_ID, applicationId);

  const [stage, setStage] = useState<Stage>();
  const [i9ErrorCode, setI9ErrorCode] = useState<ErrorCode | undefined>();

  const i9LinkAccessor = useCallback(async () => {
    const { candidateId } = await getCandidateData(applicationId);
    return getCaasInfo(candidateId, caasPath);
  }, [applicationId]);

  const requestI9Link = useCallback(async () => {
    try {
      const response = await i9LinkAccessor();

      if (response.errorCode) {
        setI9ErrorCode(response.errorCode.toString() as ErrorCode);
      } else {
        setI9ErrorCode(undefined);
      }
    } catch (e) {
      setI9ErrorCode(ErrorCode.Internal);
    }
  }, [i9LinkAccessor]);

  useEffect(() => {
    getSettings().then((settings) => setStage(settings.stage));

    const taskDefinitions = getTaskDefinitionsFromConfig(RIGHT_TO_WORK_TASK_DEFINITION_ID, applicationId);

    if (!taskDefinitions || taskDefinitions.isIntegratedWithCaaS) {
      requestI9Link();
    }
  }, []);

  const translatedStatus = bundle.getMessage("checklist-General-Status");
  const translatedDetails = bundle.getMessage("Checklist-CardControlled-DetailsTitle");

  const resolveInstructions = () => {
    const country = getRegionFromCookie() as LAUNCHED_COUNTRY;
    const jobType = getJobType(applicationId);

    const instructionMapping: { [key in LAUNCHED_COUNTRY]?: any } = {
      [LAUNCHED_COUNTRY.US]: getUSInstructionMapping({ bundle, i9ErrorCode, stage }),
      [LAUNCHED_COUNTRY.GB]: getGBInstructionMapping(),
    };

    const instruction = instructionMapping[country][jobType][status!];

    if (country == LAUNCHED_COUNTRY.GB && !instruction) {
      return <GbNonCompleteInstruction applicationId={applicationId} />;
    }

    return instruction;
  };

  const resolveDefaultChecklist = (country: LAUNCHED_COUNTRY, jobType: JobType) => {
    const defaultChecklistMapping: { [key in LAUNCHED_COUNTRY]?: any } = {
      [LAUNCHED_COUNTRY.US]: getUSChecklistMappingDefault({ bundle }),
      [LAUNCHED_COUNTRY.GB]: getGBChecklistMappingDefault({ bundle }),
    };

    const defaultChecklist = defaultChecklistMapping[country];
    const taskDefinitions = getTaskDefinitionsFromConfig(RIGHT_TO_WORK_TASK_DEFINITION_ID, applicationId);

    if (
      country === LAUNCHED_COUNTRY.GB &&
      jobType === CUSTOMER_SERVICE &&
      taskDefinitions &&
      !taskDefinitions.isIntegratedWithCaaS
    ) {
      return null;
    }

    return defaultChecklist;
  };

  const resolveChecklist = () => {
    const country = getRegionFromCookie() as LAUNCHED_COUNTRY;
    const jobType = getJobType(applicationId);

    const checklistMapping: { [key in LAUNCHED_COUNTRY]?: any } = {
      [LAUNCHED_COUNTRY.US]: getUSChecklistMappingCAAS({ bundle }),
      [LAUNCHED_COUNTRY.GB]: getGBChecklistMappingCAAS({ bundle }),
    };

    const checklist = checklistMapping[country][jobType][status!];

    if (checklist) {
      return checklist;
    }

    return resolveDefaultChecklist(country, jobType);
  };

  const instructions = resolveInstructions();
  const checklist = resolveChecklist();

  const isNotEligibleForRenderingChecklist = () => {
    const country = getRegionFromCookie() as LAUNCHED_COUNTRY;
    return (
      country !== LAUNCHED_COUNTRY.US && // TODO: should be removed once integration with CaaS is done
      isCustomerService(applicationId) &&
      taskDefinitions &&
      !taskDefinitions.isIntegratedWithCaaS
    );
  };

  return (
    <Col padding={"S300"} gridGap={"S300"}>
      {isNotEligibleForRenderingChecklist() ? null : (
        <Card padding={"S100"}>
          <Row>
            <Col backgroundColor={renderBGColor(status, shouldOverrideStatus)} width={"S200"} />
            {checklist && (
              <Col gridGap={"S300"} padding={"S300"}>
                <H3 fontSize={"T400"}>{translatedStatus}</H3>
                {checklist}
              </Col>
            )}
          </Row>
        </Card>
      )}
      {instructions && (
        <Card padding={"S100"}>
          <Col gridGap={"S300"} padding={"S300"}>
            <H3 fontSize={"T400"}>{translatedDetails}</H3>
            {instructions}
          </Col>
        </Card>
      )}
    </Col>
  );
};
